import { appPortalBaseQuery } from '@/features/baseQuery'
import { createApi } from '@reduxjs/toolkit/query/react'

import {
  AIModelInfo,
  ExtractInvoiceRequest,
  DownloadS3FileRequest,
  InvoiceDraftByIdResponse,
  InvoiceItemAllocationResult, InvoiceByIdResponse,
  SubmitInvoiceDraftRequest,
  AddInvoiceDraftItemRequest,
  EditInvoiceDraftRequest,
  allocateCostsRequest,
  UsageGroupsStructure,
  ImportProductSubscriptionRequest,
  AddProductSubscriptionRequest,
  DeleteProductSubscriptionRequest,
  SaveProductSubscriptionsRequest, ProductSubscription, VendorNameResponse, RetryInvoiceDraftRequest,
  DeleteMultipleProductSubscriptionRequest,
  AllocationConfigSnapshots,
  UserResponse,
  Fund,
  CreateFundRequest,
  UpdateFundsRequestItem,
} from './def'
import { downloadExcel } from '@/utils/misc.ts'

const AI_EXTRACT_BASE_URL = 'api/ai-invoice-extractor'
const INVOICE_DRAFT_BASE_URL = 'api/invoice-draft'
const INVOICE_BASE_URL = 'api/invoice'
const COST_ALLOCATION_BASE_URL = 'api/cost-allocation'
const PRODUCT_SUBSCRIPTION_BASE_URL = 'api/product-subscription'
const USER_BASE_URL = 'api/user'
const FILE_BASE_URL = 'api/files'
const FUND_BASE_URL = 'api/fund'


export const invoicesAllocationApi = createApi({
  reducerPath: 'invoicesAllocationApi',
  baseQuery: appPortalBaseQuery({
    baseUrl: import.meta.env.VITE_FINANCE_PORTAL_API_BASE_URL,
  }),
  tagTypes: ['InvoiceDraftItems', 'CostNatures', 'ProductDescriptions', 'Users', 'Vendors', 'InvoiceItems', 'ProductSubscriptions', 'Funds'],
  endpoints: (builder) => ({
    extractInvoice: builder.mutation<string, ExtractInvoiceRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('PdfFile', request.invoiceFile)
        formData.append('AIModel', request.model)
        formData.append('SplitPdfPage', String(request.enableSplitPdf))

        return {
          url: `${AI_EXTRACT_BASE_URL}/extract-invoice-data`,
          method: 'POST',
          body: formData,
        }
      },
    }),
    getInvoiceDraftsList: builder.query<InvoiceDraftByIdResponse[], void>({
      query: () =>({
          url: `${INVOICE_DRAFT_BASE_URL}/get-all-invoice-drafts`,
          method: 'GET',
        }),
    }),
    getInvoiceDraftById: builder.query<InvoiceDraftByIdResponse, { invoiceDraftId: string }>({
      query: ({ invoiceDraftId }) => ({
        url: `${INVOICE_DRAFT_BASE_URL}/${invoiceDraftId}`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg) =>
        result
          ? [
              ...result.invoiceDraft.invoiceDraftItems.map(({ id }) => ({ type: 'InvoiceDraftItems', id }) as const),
              { type: 'InvoiceDraftItems', id: arg.invoiceDraftId },
            ]
          : [
              { type: 'InvoiceDraftItems', id: arg.invoiceDraftId },
            ],
    }),
    addInvoiceDraftItem: builder.mutation<string, AddInvoiceDraftItemRequest>({
      query: ({ invoiceDraftId, invoiceDraftItem }) => ({
        url: `${INVOICE_DRAFT_BASE_URL}/add-draft-item`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceDraftId, invoiceDraftItem }),
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'InvoiceDraftItems', id: arg.invoiceDraftId },
        { type: 'CostNatures', id: 'LIST' },
      ],
    }),
    deleteInvoiceDraftItem: builder.mutation<void, { invoiceDraftItemId: string }>({
      query: ({ invoiceDraftItemId }) => ({
        url: `${INVOICE_DRAFT_BASE_URL}/delete-draft-item/${invoiceDraftItemId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['InvoiceDraftItems'],
    }),
    deleteMultipleInvoiceDraftItem: builder.mutation<void, { invoiceDraftItemIds: string[] }>({
      query: ({ invoiceDraftItemIds }) => ({
        url: `${INVOICE_DRAFT_BASE_URL}/delete-draft-items`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceDraftItemIds }),
      }),
      invalidatesTags: ['InvoiceDraftItems'],
    }),
    editInvoiceDraft: builder.mutation<string, EditInvoiceDraftRequest>({
      query: ({ invoiceDraftId, invoiceDraft }) => ({
        url: `${INVOICE_DRAFT_BASE_URL}/edit-draft`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceDraftId, invoiceDraft }),
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'InvoiceDraftItems', id: arg.invoiceDraftId },
        { type: 'CostNatures', id: 'LIST' },
      ],
    }),
    submitInvoiceDraft: builder.mutation<string, SubmitInvoiceDraftRequest>({
      query: (request) => ({
        url: `${INVOICE_DRAFT_BASE_URL}/submit-draft`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'InvoiceDraftItems', id: arg.invoiceDraftId },
        { type: 'CostNatures', id: 'LIST' },
        { type: 'ProductDescriptions', id: 'LIST' },
        { type: 'Vendors', id: 'LIST' },
      ],
    }),
    getCostNaturesList: builder.query<string[], void>({
      query: () => ({
        url: `${INVOICE_DRAFT_BASE_URL}/cost-natures/get-all`,
        method: 'GET',
      }),
      providesTags: [{ type: 'CostNatures', id: 'LIST' }],
    }),
    getProductDescriptionsList: builder.query<string[], void>({
      query: () => ({
        url: `${INVOICE_DRAFT_BASE_URL}/product-descriptions/get-all`,
        method: 'GET',
      }),
      providesTags: [{ type: 'ProductDescriptions', id: 'LIST' }],
    }),
    getUsersList: builder.query<UserResponse[], void>({
      query: () => ({
        url: `${USER_BASE_URL}/get-all-users`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    deactivateUser: builder.mutation<string, { userId: string }>({
      query: ({ userId }) => ({
        url: `${USER_BASE_URL}/deactivate`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      }),
      invalidatesTags: ['Users', 'ProductSubscriptions'],
    }),
    getVendorsList: builder.query<VendorNameResponse[], void>({
      query: () => ({
        url: `${INVOICE_DRAFT_BASE_URL}/vendors/get-all`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Vendors', id: 'LIST' }],
    }),
    getInvoicesList: builder.query<InvoiceByIdResponse[], void>({
      query: () =>({
        url: `${INVOICE_BASE_URL}/get-all-invoices`,
        method: 'GET',
      }),
    }),
    getInvoiceById: builder.query<InvoiceByIdResponse, { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `${INVOICE_BASE_URL}/${invoiceId}`,
        method: 'GET',
      }),
    }),
    getAIModelList: builder.query<AIModelInfo[], void>({
      query: () =>({
        url: `${AI_EXTRACT_BASE_URL}/models`,
        method: 'GET',
      }),
    }),
    allocateCosts: builder.mutation<void, allocateCostsRequest>({
      query: (allocateCostsRequest) => ({
        url: `${COST_ALLOCATION_BASE_URL}/allocate-with-rules`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(allocateCostsRequest),
      }),
    }),
    getInvoiceItemAllocationResultsByInvoiceId: builder.query<InvoiceItemAllocationResult[], { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `${COST_ALLOCATION_BASE_URL}/${invoiceId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'InvoiceItems', id: arg.invoiceId },
      ],
    }),
    getUsageGroupsStructure: builder.query<UsageGroupsStructure[], { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `${COST_ALLOCATION_BASE_URL}/get-usage-groups-structure/${invoiceId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'InvoiceItems', id: arg.invoiceId },
      ],
    }),
    getAllocationConfigSnapshots: builder.query<AllocationConfigSnapshots[], { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `${COST_ALLOCATION_BASE_URL}/get-allocation-config-snapshots/${invoiceId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'InvoiceItems', id: arg.invoiceId },
      ],
    }),
    getCostAllocationReportsList: builder.query<void, void>({
      query: () => ({
        url: '/api/getCostAllocationReportsList',
        method: 'GET',
      }),
    }),
    downloadCostAllocationReportById: builder.mutation<Blob, { id: string }>({
      query: ({ id }) => ({
        url: `/api/downloadCostAllocationReportById?id=${encodeURIComponent(id)}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    downloadCostAllocationReportByInvoiceId: builder.mutation<Blob, { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `/api/report/cost-allocation?invoiceId=${encodeURIComponent(invoiceId)}`,
        method: 'GET',
        responseHandler: async (response) => {
          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.detail)
          }
          const blob = await response.blob()
          downloadExcel(`cost_allocations_${invoiceId}`, blob)
        },
      }),
    }),
    retryInvoiceDraft: builder.mutation<void, RetryInvoiceDraftRequest>({
      query: (retryInvoiceDraftRetryDto) => ({
        url: `${AI_EXTRACT_BASE_URL}/retry-invoice-draft`,
        method: 'POST',
        body: retryInvoiceDraftRetryDto,
      }),
    }),
    getProductSubscriptions: builder.query<ProductSubscription[], void>({
      query: () => ({
        url: `${PRODUCT_SUBSCRIPTION_BASE_URL}/get-all-subscriptions`,
        method: 'GET',
      }),
      providesTags: ['ProductSubscriptions'],
    }),
    importProductSubscription: builder.mutation<void, ImportProductSubscriptionRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('excelFile', request.file)
        return {
          url: `${PRODUCT_SUBSCRIPTION_BASE_URL}/parse-subscriptions`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['ProductSubscriptions', 'Users'],
    }),
    saveProductSubscriptions: builder.mutation<void, SaveProductSubscriptionsRequest>({
      query: (request) => ({
        url: `${PRODUCT_SUBSCRIPTION_BASE_URL}/update-subscriptions`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }),
      invalidatesTags: ['ProductSubscriptions'],
    }),
    addProductSubscription: builder.mutation<void, AddProductSubscriptionRequest>({
      query: (request) => ({
        url: `${PRODUCT_SUBSCRIPTION_BASE_URL}/add-subscriptions`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['ProductSubscriptions'],
    }),
    deleteProductSubscription: builder.mutation<void, DeleteProductSubscriptionRequest>({
      query: (request) => ({
        url: `${PRODUCT_SUBSCRIPTION_BASE_URL}/delete-subscriptions`,
        method: 'DELETE',
        body: request,
      }),
      invalidatesTags: ['ProductSubscriptions'],
    }),
    deleteMultipleProductSubscription: builder.mutation<void, DeleteMultipleProductSubscriptionRequest>({
      query: (request) => ({
        url: `${PRODUCT_SUBSCRIPTION_BASE_URL}/delete-multiple-subscriptions`,
        method: 'DELETE',
        body: request,
      }),
      invalidatesTags: ['ProductSubscriptions'],
    }),
    downloadS3File: builder.mutation<Blob, DownloadS3FileRequest>({
      query: (request) => ({
        url: `${FILE_BASE_URL}/download-s3`,
        method: 'POST',
        body: request,
        responseHandler: (response) => response.blob(),
      }),
    }),
    getAllFunds: builder.query<Fund[], void>({
      query: () => ({
        url: `${FUND_BASE_URL}/get-all-funds`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Funds', id: 'LIST' }],
    }),
    addFund: builder.mutation<void, CreateFundRequest>({
      query: (request) => ({
        url: `${FUND_BASE_URL}/add-fund`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }),
      invalidatesTags: [{ type: 'Funds', id: 'LIST' }],
    }),
    updateFunds: builder.mutation<void, UpdateFundsRequestItem[]>({
      query: (request) => ({
        url: `${FUND_BASE_URL}/update-funds`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      }),
      invalidatesTags: [{ type: 'Funds', id: 'LIST' }],
    }),
    deleteFund: builder.mutation<void, { fundId: string }>({
      query: ({ fundId }) => ({
        url: `${FUND_BASE_URL}/delete-fund/${fundId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Funds', id: 'LIST' }],
    }),
    getAllFundsName: builder.query<string[], void>({
      query: () => ({
        url: `${FUND_BASE_URL}/get-all-fund-names`,
        method: 'GET',
      }),
      providesTags: [{ type: 'Funds', id: 'LIST' }],
    }),
  }),
})

export const {
  useExtractInvoiceMutation,
  useGetInvoiceDraftByIdQuery,
  useAddInvoiceDraftItemMutation,
  useDeleteInvoiceDraftItemMutation,
  useDeleteMultipleInvoiceDraftItemMutation,
  useSubmitInvoiceDraftMutation,
  useEditInvoiceDraftMutation,
  useGetCostNaturesListQuery,
  useGetProductDescriptionsListQuery,
  useGetUsersListQuery,
  useDeactivateUserMutation,
  useGetVendorsListQuery,
  useGetInvoicesListQuery,
  useGetInvoiceByIdQuery,
  useAllocateCostsMutation,
  useGetInvoiceItemAllocationResultsByInvoiceIdQuery,
  useGetUsageGroupsStructureQuery,
  useGetAllocationConfigSnapshotsQuery,
  useGetCostAllocationReportsListQuery,
  useDownloadCostAllocationReportByIdMutation,
  useDownloadCostAllocationReportByInvoiceIdMutation,
  useRetryInvoiceDraftMutation,
  useGetInvoiceDraftsListQuery,
  useGetAIModelListQuery,
  useGetProductSubscriptionsQuery,
  useImportProductSubscriptionMutation,
  useSaveProductSubscriptionsMutation,
  useAddProductSubscriptionMutation,
  useDeleteProductSubscriptionMutation,
  useDeleteMultipleProductSubscriptionMutation,
  useDownloadS3FileMutation,
  useGetAllFundsQuery,
  useGetAllFundsNameQuery,
  useAddFundMutation,
  useUpdateFundsMutation,
  useDeleteFundMutation,
} = invoicesAllocationApi
